import { z } from "zod";
import { ORG_ROLES, ROLES } from "./roles.js";
/**
 * Zod schemas for every DTO crossing the network. Shared between
 * server (validation) and client (form types).
 *
 * Per FD `business-rules.md § validation rules`.
 */
const trimmedString = (min, max) => z.string().trim().min(min).max(max);
/** Email — RFC 5321 length bound + the validator's email check. */
export const emailSchema = z.string().trim().toLowerCase().email().min(5).max(254);
/** Organization name — 1-80 chars, trimmed. UTF-8 friendly. */
export const orgNameSchema = trimmedString(1, 80);
/**
 * Workspace slug — lowercase alphanumeric + hyphen, 3-32 chars,
 * no leading/trailing hyphen, no consecutive hyphens.
 */
export const slugSchema = z
    .string()
    .regex(/^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/, "Invalid workspace slug format")
    .refine((s) => !s.includes("--"), "Slug cannot contain consecutive hyphens");
export const roleSchema = z.enum(ROLES);
// ----- Authentication endpoints -----
/**
 * Email + password registration (ENG-35588). Replaces the magic-link
 * `signupSchema` at the network boundary — same endpoint name
 * (`POST /api/auth/signup`), new payload shape.
 *
 * Password length matches WorkOS's policy: min 8, max 128. The provider
 * enforces additional rules (breached-password block) on its side.
 */
export const registerWithPasswordSchema = z.object({
    email: emailSchema,
    password: z.string().min(8, "Password must be at least 8 characters").max(128),
});
/**
 * One-time code email-verification (ENG-35588). Pairs with
 * `registerWithPasswordSchema` — the user receives a 6-digit OTC and
 * posts it back here to mark their email verified.
 */
export const verifyEmailSchema = z.object({
    email: emailSchema,
    code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code from your email"),
});
/**
 * Resend OTC for an unverified email (ENG-35588). Rate-limited
 * provider-side (3 / 10min per email). Uniform response shape on
 * known/unknown email to prevent enumeration.
 */
export const resendVerificationSchema = z.object({
    email: emailSchema,
});
/**
 * @deprecated Magic-link signup (M3). Kept for the
 *   `auth.signup(orgName, email)` method on `AuthService` which is no
 *   longer called by any controller — see TODO(magic-link) in
 *   `auth.controller.ts`. Will be removed if magic-link is not
 *   reintroduced.
 */
export const signupSchema = z.object({
    email: emailSchema,
    orgName: orgNameSchema,
});
/**
 * @deprecated Magic-link request (M3). Endpoint commented out in
 *   `auth.controller.ts`. Schema kept for the preserved
 *   `AuthService.requestMagicLink` method.
 */
export const loginMagicSchema = z.object({
    email: emailSchema,
});
export const loginPasswordSchema = z.object({
    email: emailSchema,
    /** Length-only check here; provider enforces strength policy. */
    password: z.string().min(1).max(256),
});
/**
 * 6-digit one-time code that WorkOS Magic Auth emails to the user.
 * The dashboard's check-email page collects it via a code-entry input.
 */
export const verifyMagicCodeSchema = z.object({
    email: emailSchema,
    code: z.string().regex(/^\d{6}$/, "Enter the 6-digit code from your email"),
});
export const joinSchema = z.object({
    token: z.string().min(1),
});
// ----- Workspace endpoints -----
/**
 * Optional workspace icon/logo URL. An empty string clears it (→ null); a
 * present value must be a valid http(s) URL (≤ 2048 chars). Omitted leaves the
 * stored value untouched on update. A broken/unreachable URL is tolerated by
 * the UI (it falls back to the default icon), so we only validate the shape.
 */
export const logoUrlSchema = z
    .string()
    .trim()
    .max(2048)
    .url("Enter a valid URL")
    .or(z.literal(""))
    .nullable()
    .optional()
    .transform((v) => (v === "" ? null : v));
export const createWorkspaceSchema = z.object({
    name: orgNameSchema,
    logoUrl: logoUrlSchema,
});
export const updateWorkspaceSchema = z.object({
    name: orgNameSchema.optional(),
    logoUrl: logoUrlSchema,
});
export const switchWorkspaceSchema = z.object({
    slug: slugSchema,
});
// ----- Member / invitation endpoints -----
/** Default role for new invitations is `member` per FD R6. */
export const inviteMemberSchema = z.object({
    email: emailSchema,
    // Built-in workspace role key OR a custom `RoleDefinition.id` (B3); the
    // service validates it against the built-ins + the workspace's custom roles.
    role: z.string().trim().min(1).max(64).default("member"),
});
// Accepts any role string: a built-in workspace role key (admin / member /
// agent_editor / …) OR a custom `RoleDefinition.id`. MembershipService
// validates it against the built-ins + the workspace's custom roles.
export const changeRoleSchema = z.object({
    role: z.string().trim().min(1).max(64),
});
// ----- Org-tier role management -----
// Org roles are a fixed, flat set (no custom org roles), so unlike
// `changeRoleSchema` this is a strict enum. `OrgMembershipService.changeRole`
// re-checks with `isOrgRole` as defense-in-depth.
export const changeOrgRoleSchema = z.object({
    role: z.enum(ORG_ROLES),
});
// ----- Custom-role management (B3) -----
/** Create/update payload for a custom workspace role. Subset-of-catalogue and
 * reserved-name rules are enforced server-side by RoleDefinitionService. */
export const roleDefinitionInputSchema = z.object({
    name: trimmedString(1, 40),
    permissions: z.array(z.string()).min(1),
});
const builtinRoleSchema = z.object({
    name: z.string(),
    permissions: z.array(z.string()),
});
const customRoleSchema = z.object({
    id: z.string(),
    name: z.string(),
    permissions: z.array(z.string()),
    /** 'workspace' | 'helpdesk' — desk-scoped roles are authored on the desk's
     *  own Roles page and shown with a scope badge on workspace surfaces. */
    scope: z.enum(["workspace", "helpdesk"]).optional(),
});
export const roleListResponseSchema = z.object({
    builtin: z.array(builtinRoleSchema),
    custom: z.array(customRoleSchema),
});
/** Workspace-grantable permission slugs, grouped by subject, for the builder. */
export const permissionCatalogueSchema = z.object({
    groups: z.array(z.object({ subject: z.string(), permissions: z.array(z.string()) })),
});
export const roleDeletedSchema = z.object({ repointed: z.number().int() });
// ----- In-app notifications (#7) -----
export const notificationSchema = z.object({
    id: z.string(),
    type: z.string(),
    data: z.record(z.unknown()),
    read: z.boolean(),
    createdAt: z.string(),
});
export const notificationListResponseSchema = z.object({
    notifications: z.array(notificationSchema),
    unreadCount: z.number().int(),
});
export const notificationsReadResponseSchema = z.object({ markedRead: z.number().int() });
// ----- Feature-flag administration (B3) -----
const flagValueSchema = z.union([z.boolean(), z.number(), z.string()]);
export const featureFlagAdminSchema = z.object({
    key: z.string(),
    kind: z.string(),
    description: z.string(),
    internal_only: z.boolean(),
    default_value: flagValueSchema,
    value: flagValueSchema,
    source: z.enum(["workspace", "org", "default"]),
});
export const featureFlagListResponseSchema = z.object({
    flags: z.array(featureFlagAdminSchema),
});
export const setFeatureFlagSchema = z.object({
    value: flagValueSchema,
    scope: z.enum(["workspace", "org"]).default("workspace"),
});
export const featureFlagSetResponseSchema = z.object({
    key: z.string(),
    value: flagValueSchema,
    scope: z.enum(["workspace", "org"]),
});
export const featureFlagClearedSchema = z.object({ cleared: z.boolean() });
// ----- API key endpoints -----
/**
 * `expiresAt` can be:
 *  - omitted / null → never expires
 *  - ISO-8601 timestamp string → absolute expiry; must be >= 1h in the
 *    future and after `createdAt`
 *
 * Shortcuts like `'30d'` / `'90d'` are resolved client-side before posting.
 */
export const createApiKeySchema = z.object({
    name: trimmedString(1, 60),
    expiresAt: z.string().datetime({ offset: true }).nullable().optional(),
});
export const updateApiKeySchema = z.object({
    name: trimmedString(1, 60),
});
//# sourceMappingURL=schemas.js.map